import type { PlanType } from '~/resources/graphql/graphql'
import { useLayoutMode } from '~/composables/use-layout-mode'

export function useRouteLink() {
    const { preserveModeLink } = useLayoutMode()

    return {
        orgHomeLink() {
            return preserveModeLink('/')
        },

        orgSignInLink(redirect: string) {
            return preserveModeLink(
                `/sign-in?redirect=${encodeURIComponent(redirect)}`
            )
        },

        emptyLink() {
            return preserveModeLink(unsafe_emptyLink())
        },

        facilityHomeLink(facilitySlug: string) {
            return preserveModeLink(`/${facilitySlug}`)
        },

        facilitySignInLink(facilitySlug: string) {
            return preserveModeLink(`/${facilitySlug}/sign-in`)
        },

        facilitySignInRedirectLink(facilitySlug: string, redirect: string) {
            return preserveModeLink(
                `/${facilitySlug}/sign-in?redirect=${encodeURIComponent(
                    redirect
                )}`
            )
        },

        facilityCartLink(facilitySlug: string) {
            return preserveModeLink(unsafe_facilityCartLink(facilitySlug))
        },

        facilityMembershipLink(facilitySlug: string, membershipId: string) {
            return preserveModeLink(
                `/${facilitySlug}/memberships/${membershipId}`
            )
        },

        facilityProgramLink(facilitySlug: string, programSlug: string) {
            return preserveModeLink(`/${facilitySlug}/programs/${programSlug}`)
        },

        facilityCourseLink(
            facilitySlug: string,
            programSlug: string,
            params: {
                date?: string
                course?: string
                linkedCourse?: string
            }
        ) {
            return preserveModeLink(
                `/${facilitySlug}/programs/${programSlug}?${new URLSearchParams(
                    params
                ).toString()}`
            )
        },

        facilityPassLink(facilitySlug: string, programSlug: string) {
            return preserveModeLink(`/${facilitySlug}/passes/${programSlug}`)
        },

        facilityCheckoutLink(facilitySlug: string, checkoutId: string) {
            return preserveModeLink(`/${facilitySlug}/checkout/${checkoutId}`)
        },

        facilityEditContractLink(
            facilitySlug: string,
            planType: PlanType,
            contractId: string
        ) {
            if (planType === 'AGREEMENT') {
                return preserveModeLink(
                    `/${facilitySlug}/agreement/${contractId}`
                )
            }

            if (planType === 'MEMBERSHIP') {
                return preserveModeLink(
                    `/${facilitySlug}/membership/${contractId}`
                )
            }

            if (planType === 'PASS') {
                return preserveModeLink(`/${facilitySlug}/pass/${contractId}`)
            }

            if (planType === 'PROGRAM') {
                return preserveModeLink(
                    `/${facilitySlug}/booking/${contractId}`
                )
            }

            if (planType === 'GIFTCARD') {
                return preserveModeLink(
                    `/${facilitySlug}/gift-card/${contractId}`
                )
            }

            throw new Error()
        },

        // Note: there is an equivalent server-side function in CPXLinkHelpers.php
        facilityCreateContractLink(
            storefrontPrefix: string,
            planType: PlanType,
            slug: string
        ) {
            if (planType === 'AGREEMENT') {
                return preserveModeLink(
                    `${storefrontPrefix}/agreements/${slug}`
                )
            }

            if (planType === 'MEMBERSHIP') {
                return preserveModeLink(
                    `${storefrontPrefix}/memberships/${slug}`
                )
            }

            if (planType === 'PASS') {
                return preserveModeLink(`${storefrontPrefix}/passes/${slug}`)
            }

            if (planType === 'PROGRAM') {
                return preserveModeLink(`${storefrontPrefix}/programs/${slug}`)
            }

            if (planType === 'GIFTCARD') {
                return preserveModeLink(
                    `${storefrontPrefix}/gift-cards/${slug}`
                )
            }

            throw new Error()
        },

        storefrontHomeLink(storefrontPrefix: string) {
            return preserveModeLink(`${storefrontPrefix}`)
        },

        storefrontNodeLink(storefrontPrefix: string, slug: string) {
            return preserveModeLink(`${storefrontPrefix}/n/${slug}`)
        },

        storefrontCalendarGroupLink(
            storefrontPrefix: string,
            parentSlug: string,
            groupSlug: string
        ) {
            return preserveModeLink(
                `${storefrontPrefix}/n/${parentSlug}?group=${groupSlug}`
            )
        },

        profileLink() {
            return preserveModeLink(`/profile`)
        },

        profileSettingsLink() {
            return preserveModeLink(`/profile/settings`)
        },

        // Inbox ownership follows the signed-in profile, never the selected family member.
        profileInboxLink() {
            return preserveModeLink('/profile/inbox')
        },

        profileInboxMessageLink(id: string) {
            return preserveModeLink(`/profile/inbox/${encodeURIComponent(id)}`)
        },

        profileInvoiceLink(id: string) {
            return preserveModeLink(`/profile/invoices/${id}`)
        },

        profileInvoicesLink() {
            return preserveModeLink(`/profile/account/orders`)
        },

        profilePaymentsLink() {
            return preserveModeLink(`/profile/account/payments`)
        },

        profileContractsLink() {
            return preserveModeLink('/profile/contracts')
        },

        // The combined list keeps each contract's existing detail URL so saved links remain valid.
        profileContractLink(contract: { id: string; planType: PlanType }) {
            if (contract.planType === 'MEMBERSHIP') {
                return preserveModeLink(`/profile/memberships/${contract.id}`)
            }

            if (contract.planType === 'PROGRAM') {
                return preserveModeLink(`/profile/bookings/${contract.id}`)
            }

            if (contract.planType === 'PASS') {
                return preserveModeLink(`/profile/passes/${contract.id}`)
            }

            return null
        },

        profileMembershipLink(id: string) {
            return preserveModeLink(`/profile/memberships/${id}`)
        },

        profileMembershipsLink() {
            return preserveModeLink(`/profile/memberships`)
        },

        // Note: this generated on the PHP side as well
        // https://github.com/redpointhq/rphq-redpoint-api-v1/blob/4bae7d3f3189600fa647b1adc85d121819b1022d/app/Domain/Plans/Models/Contract.php#L481
        profileBookingLink(id: string) {
            return preserveModeLink(`/profile/bookings/${id}`)
        },

        profileBookingsLink() {
            return preserveModeLink(`/profile/bookings`)
        },

        profilePassLink(id: string) {
            return preserveModeLink(`/profile/passes/${id}`)
        },

        profilePassesLink() {
            return preserveModeLink('/profile/passes')
        },

        profileAgreementLink(id: string) {
            return preserveModeLink(`/profile/agreements/${id}`)
        },

        profileAgreementsLink() {
            return preserveModeLink(`/profile/agreements`)
        },

        profilePaymentMethodsLink() {
            return preserveModeLink(`/profile/payment-methods`)
        },

        profileFriendsAndFamilyLink() {
            return preserveModeLink(`/profile/friends-and-family`)
        },

        profileCustomerInfoLink() {
            return preserveModeLink(`/profile/customer-info`)
        },

        profileCheckInHistoryLink() {
            return preserveModeLink(`/profile/check-in-history`)
        },

        profileBenefitsAndDiscountsLink() {
            return preserveModeLink(`/profile/benefits`)
        },

        profileWaitlistsLink() {
            return preserveModeLink(`/profile/waitlists`)
        },

        profileContactUsLink() {
            return preserveModeLink(`/profile/contact-us`)
        },

        profileCheckInLink() {
            return preserveModeLink(`/profile/check-in`)
        },

        profileDashboardLink() {
            return preserveModeLink(`/profile/staff/dashboard`)
        },

        // Note: this is generated on the PHP side as well on the Contract model
        // https://github.com/redpointhq/rphq-redpoint-api-v1/blob/4bae7d3f3189600fa647b1adc85d121819b1022d/app/Domain/Plans/Models/Contract.php#L493
        groupStorefrontLink(contractId: string, facilitySlug: string) {
            return preserveModeLink(`/group-${contractId}/${facilitySlug}`)
        },

        publicGroupBookingLink(id: string, facilitySlug: string) {
            return preserveModeLink(`/${facilitySlug}/group-booking/${id}`)
        }
    }
}

export function unsafe_facilityCreateContractLink(
    storefrontPrefix: string,
    planType: PlanType,
    slug: string
) {
    if (planType === 'AGREEMENT') {
        return `${storefrontPrefix}/agreements/${slug}`
    }

    if (planType === 'MEMBERSHIP') {
        return `${storefrontPrefix}/memberships/${slug}`
    }

    if (planType === 'PASS') {
        return `${storefrontPrefix}/passes/${slug}`
    }

    if (planType === 'PROGRAM') {
        return `${storefrontPrefix}/programs/${slug}`
    }

    if (planType === 'GIFTCARD') {
        return `${storefrontPrefix}/gift-cards/${slug}`
    }

    throw new Error()
}

export function unsafe_facilityCartLink(facilitySlug: string) {
    return `/${facilitySlug}/cart`
}

export function unsafe_emptyLink() {
    return `/empty`
}

export function unsafe_checkInLink() {
    return `/profile/check-in`
}

export function layoutModeLightboxLink(url: string) {
    return '/lightbox' + url
}

export function layoutModeKioskLink(kioskSlug: string, url: string) {
    return '/kiosk-' + kioskSlug + url
}

export function layoutModeGroupLink(groupId: string, url: string) {
    return '/group-' + groupId + url
}

export function locationOriginWithoutMobile() {
    return location.origin.replace('-mobile', '')
}
